import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Tooltip,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    AssignmentInd as AssignIcon,
    Visibility as VisibilityIcon,
    ContentCopy as CopyIcon,
    AutoAwesome as AiIcon,
    ExpandMore as ExpandMoreIcon,
    Quiz as QuizIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { questionnaireService, IQuestionnaire, IGeneratedQuestionnaireDraft } from '../services/questionnaireService';
import GenerateQuestionnaireModal from '../components/GenerateQuestionnaireModal';
import { userListService } from '../services/userListService';
import { IUserTreeNode } from '../types/userList';
import { useAuthStore } from '../stores/authStore';
import { stitchColors } from '../theme/tokens';

const QuestionnaireManagement: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [generateModalOpen, setGenerateModalOpen] = useState(false);
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<IQuestionnaire | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<IQuestionnaire | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [detailsQuestionnaire, setDetailsQuestionnaire] = useState<IQuestionnaire | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Fetch questionnaires
    const {
        data: questionnaires = [],
        isLoading,
        error: fetchError
    } = useQuery<IQuestionnaire[]>({
        queryKey: ['questionnaires'],
        queryFn: () => questionnaireService.getQuestionnaires(),
        enabled: !!user
    });

    // Active questionnaire
    const activeQuestionnaire = useMemo(() => {
        return questionnaires.find(q => q.isActive);
    }, [questionnaires]);

    // Fetch user nodes for manual assignment
    const [userNodes, setUserNodes] = useState<IUserTreeNode[]>([]);
    const [userNodesLoading, setUserNodesLoading] = useState(false);

    useEffect(() => {
        const fetchUserNodes = async () => {
            setUserNodesLoading(true);
            try {
                const lists = await userListService.getLists();
                const list = lists[0];
                if (!list) return;
                const nodes = await userListService.getNodes(list._id);
                setUserNodes(nodes.filter(n => !n.parentId).map(n => ({ ...n, _id: n._id.toString() })));
            } catch {
                // Silent fail
            } finally {
                setUserNodesLoading(false);
            }
        };
        fetchUserNodes();
    }, []);

    const filteredQuestionnaires = useMemo(() => {
        return questionnaires.filter(q =>
            q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (q.description || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [questionnaires, searchTerm]);

    // Set active mutation
    const setActiveMutation = useMutation({
        mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
            questionnaireService.setActiveQuestionnaire(id, isActive),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
            setNotification({
                type: 'success',
                message: variables.isActive
                    ? 'Questionnaire marked as active. All clients under your account must now complete this welcome questionnaire upon logging in.'
                    : 'Questionnaire deactivated. Clients will now see the global welcome questionnaire.'
            });
        },
        onError: () => {
            setNotification({ type: 'error', message: 'Failed to update active questionnaire status.' });
        }
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (id: string) => questionnaireService.deleteQuestionnaire(id),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
            setNotification({
                type: 'success',
                message: data.deactivated ? 'Questionnaire deactivated (has assignments)' : 'Questionnaire deleted successfully'
            });
            setDeleteConfirmOpen(false);
            setDeleteTarget(null);
        },
        onError: () => {
            setNotification({ type: 'error', message: 'Failed to delete questionnaire' });
        }
    });

    // Assign mutation
    const assignMutation = useMutation({
        mutationFn: ({ id, userIds }: { id: string; userIds: string[] }) =>
            questionnaireService.assignQuestionnaireToUsers(id, userIds),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
            setNotification({
                type: 'success',
                message: `Assigned to ${data.assigned} user(s)${data.errors ? `, ${data.errors.length} failed` : ''}`
            });
            setAssignDialogOpen(false);
            setSelectedQuestionnaire(null);
            setSelectedUserIds([]);
        },
        onError: () => {
            setNotification({ type: 'error', message: 'Failed to assign questionnaire' });
        }
    });

    // Duplicate mutation
    const duplicateMutation = useMutation({
        mutationFn: (id: string) => questionnaireService.duplicateQuestionnaire(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
            setNotification({
                type: 'success',
                message: 'Questionnaire duplicated successfully'
            });
        },
        onError: () => {
            setNotification({ type: 'error', message: 'Failed to duplicate questionnaire' });
        }
    });

    const handleToggleActive = (questionnaire: IQuestionnaire) => {
        const nextState = !questionnaire.isActive;
        setActiveMutation.mutate({ id: questionnaire._id, isActive: nextState });
    };

    const handleOpenDetails = (questionnaire: IQuestionnaire) => {
        setDetailsQuestionnaire(questionnaire);
        setDetailsDialogOpen(true);
    };

    const handleOpenAssignDialog = (questionnaire: IQuestionnaire) => {
        setSelectedQuestionnaire(questionnaire);
        setSelectedUserIds([]);
        setAssignDialogOpen(true);
    };

    const handleAssign = () => {
        if (!selectedQuestionnaire || selectedUserIds.length === 0) return;
        assignMutation.mutate({ id: selectedQuestionnaire._id, userIds: selectedUserIds });
    };

    const handleDelete = (questionnaire: IQuestionnaire) => {
        setDeleteTarget(questionnaire);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        deleteMutation.mutate(deleteTarget._id);
    };

    const handleCopyFromWelcome = () => {
        navigate('/questionnaire-library/create?copyFromWelcome=true');
    };

    const handleGeneratedDraft = (draft: IGeneratedQuestionnaireDraft) => {
        navigate('/questionnaire-library/create', { state: { initialDraft: draft } });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stitchColors.onBackground }}>
                        Welcome Questionnaires
                    </Typography>
                    <Typography variant="body2" sx={{ color: stitchColors.onSurfaceVariant, mt: 0.5 }}>
                        Create and manage welcome questionnaires for your clients. Select one as active to require clients to complete it upon login.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AiIcon />}
                        onClick={() => setGenerateModalOpen(true)}
                        sx={{ borderRadius: 1.5 }}
                    >
                        Generate from File (AI)
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<CopyIcon />}
                        onClick={handleCopyFromWelcome}
                        sx={{ borderRadius: 1.5 }}
                    >
                        Copy Global Questionnaire
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/questionnaire-library/create')}
                        sx={{ bgcolor: stitchColors.primary, borderRadius: 1.5 }}
                    >
                        Create Questionnaire
                    </Button>
                </Box>
            </Box>

            {fetchError && <Alert severity="error" sx={{ mb: 3 }}>Failed to load questionnaires</Alert>}
            {notification && (
                <Alert
                    severity={notification.type}
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setNotification(null)}
                >
                    {notification.message}
                </Alert>
            )}

            {/* Active Status Summary Banner */}
            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 2.5,
                    border: `1px solid ${activeQuestionnaire ? '#a7f3d0' : stitchColors.outlineVariant}`,
                    bgcolor: activeQuestionnaire ? '#f0fdf4' : stitchColors.surfaceContainerLowest,
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    bgcolor: activeQuestionnaire ? '#10b981' : stitchColors.surfaceVariant,
                                    color: activeQuestionnaire ? '#ffffff' : stitchColors.onSurfaceVariant,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <QuizIcon sx={{ fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: stitchColors.onBackground }}>
                                        {activeQuestionnaire ? activeQuestionnaire.title : 'Global Welcome Questionnaire Active'}
                                    </Typography>
                                    <Chip
                                        label={activeQuestionnaire?.isSystemDefault ? 'ACTIVE (SYSTEM DEFAULT)' : (activeQuestionnaire ? 'ACTIVE ONBOARDING' : 'DEFAULT FALLBACK')}
                                        color={activeQuestionnaire ? 'success' : 'default'}
                                        size="small"
                                        sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                    />
                                </Box>
                                <Typography variant="body2" sx={{ color: stitchColors.onSurfaceVariant, mt: 0.5 }}>
                                    {activeQuestionnaire?.isSystemDefault
                                        ? 'The platform default welcome questionnaire created by Super Admin is active. All clients under your lawyer account will complete this questionnaire upon login unless you activate a custom questionnaire.'
                                        : activeQuestionnaire
                                            ? 'All users under your lawyer account must complete this active questionnaire before accessing platform features.'
                                            : 'No custom questionnaire is currently marked active. Your clients will automatically complete the Global Welcome Questionnaire.'}
                                </Typography>
                            </Box>
                        </Box>

                        {activeQuestionnaire && (
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignSelf: { xs: 'flex-start', md: 'center' } }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => navigate(`/questionnaire-library/preview/${activeQuestionnaire._id}`)}
                                    sx={{ borderRadius: 1 }}
                                >
                                    View Live Form
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={activeQuestionnaire.isSystemDefault ? <CopyIcon /> : <EditIcon />}
                                    onClick={() => {
                                        if (activeQuestionnaire.isSystemDefault) {
                                            navigate('/questionnaire-library/create?copyFromWelcome=true');
                                        } else {
                                            navigate(`/questionnaire-library/edit/${activeQuestionnaire._id}`);
                                        }
                                    }}
                                    sx={{ borderRadius: 1 }}
                                >
                                    {activeQuestionnaire.isSystemDefault ? 'Customize Default' : 'Edit'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Search Input */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search questionnaires by title or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                />
            </Box>

            {/* Questionnaire Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, border: `1px solid ${stitchColors.outlineVariant}`, overflow: 'hidden' }}>
                <Table>
                    <TableHead sx={{ bgcolor: stitchColors.surfaceContainerLow }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Active Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Title &amp; Description</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Steps</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Questions</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Completed</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredQuestionnaires.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                    <QuizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {searchTerm ? 'No matching questionnaires found.' : 'No custom questionnaires yet. Create your first one or copy from the global welcome questionnaire!'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredQuestionnaires.map((questionnaire) => {
                                const totalFields = (questionnaire.steps || []).reduce(
                                    (acc, step) => acc + (step.fields ? step.fields.length : 0),
                                    0
                                );

                                return (
                                    <TableRow
                                        key={questionnaire._id}
                                        hover
                                        sx={{
                                            bgcolor: questionnaire.isActive ? '#f0fdf4' : 'inherit',
                                            transition: 'background-color 0.2s',
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Tooltip title={questionnaire.isActive ? 'Active onboarding questionnaire' : 'Set as active welcome questionnaire'}>
                                                    <Switch
                                                        checked={questionnaire.isActive}
                                                        onChange={() => handleToggleActive(questionnaire)}
                                                        disabled={setActiveMutation.isPending}
                                                        color="success"
                                                        size="small"
                                                    />
                                                </Tooltip>
                                                <Chip
                                                    label={questionnaire.isActive ? 'ACTIVE' : 'INACTIVE'}
                                                    size="small"
                                                    color={questionnaire.isActive ? 'success' : 'default'}
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.6875rem',
                                                        height: 22,
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: stitchColors.onBackground,
                                                        cursor: 'pointer',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                    onClick={() => handleOpenDetails(questionnaire)}
                                                >
                                                    {questionnaire.title}
                                                </Typography>
                                                {questionnaire.isSystemDefault && (
                                                    <Chip
                                                        label="SYSTEM DEFAULT"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '0.625rem',
                                                            height: 20,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {questionnaire.description && (
                                                <Typography variant="body2" sx={{ color: stitchColors.onSurfaceVariant, fontSize: '0.8125rem', mt: 0.25 }}>
                                                    {questionnaire.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={`${questionnaire.steps?.length || 0} step(s)`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={`${totalFields} field(s)`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={questionnaire.assignmentCounts?.completed || 0}
                                                size="small"
                                                color={questionnaire.assignmentCounts?.completed ? 'success' : 'default'}
                                                variant={questionnaire.assignmentCounts?.completed ? 'filled' : 'outlined'}
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: stitchColors.onSurfaceVariant }}>
                                                {new Date(questionnaire.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title="View Live Form (User View)">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => navigate(`/questionnaire-library/preview/${questionnaire._id}`)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Summary & Structure">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenDetails(questionnaire)}
                                                    >
                                                        <QuizIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={questionnaire.isSystemDefault ? "Duplicate & Customize" : "Edit Questionnaire"}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            if (questionnaire.isSystemDefault) {
                                                                navigate('/questionnaire-library/create?copyFromWelcome=true');
                                                            } else {
                                                                navigate(`/questionnaire-library/edit/${questionnaire._id}`);
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Duplicate Questionnaire">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => duplicateMutation.mutate(questionnaire._id)}
                                                        disabled={duplicateMutation.isPending}
                                                    >
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Assign to Specific Users">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenAssignDialog(questionnaire)}
                                                    >
                                                        <AssignIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={questionnaire.isSystemDefault ? "System default cannot be deleted" : "Delete Questionnaire"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={Boolean(questionnaire.isSystemDefault)}
                                                            onClick={() => handleDelete(questionnaire)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* View Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {detailsQuestionnaire?.title}
                        </Typography>
                        {detailsQuestionnaire?.isSystemDefault && (
                            <Chip label="SYSTEM DEFAULT" color="primary" variant="outlined" size="small" sx={{ fontWeight: 700, fontSize: '0.6875rem' }} />
                        )}
                        {detailsQuestionnaire?.isActive && (
                            <Chip label="ACTIVE" color="success" size="small" sx={{ fontWeight: 700 }} />
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Version {detailsQuestionnaire?.version || 1}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {detailsQuestionnaire?.description && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                Description
                            </Typography>
                            <Typography variant="body1">
                                {detailsQuestionnaire.description}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Steps &amp; Questions ({detailsQuestionnaire?.steps?.length || 0} Steps)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Total {(detailsQuestionnaire?.steps || []).reduce((acc, s) => acc + (s.fields?.length || 0), 0)} Questions
                        </Typography>
                    </Box>

                    {detailsQuestionnaire?.steps?.map((step, stepIndex) => (
                        <Accordion key={step.id || stepIndex} defaultExpanded={stepIndex === 0} sx={{ mb: 1.5, '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: stitchColors.surfaceContainerLow }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Chip label={`Step ${stepIndex + 1}`} size="small" sx={{ fontWeight: 600 }} />
                                    <Typography sx={{ fontWeight: 600 }}>{step.title || 'Untitled Step'}</Typography>
                                    {step.section && (
                                        <Typography variant="caption" color="text.secondary">
                                            ({step.section})
                                        </Typography>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2.5 }}>
                                {step.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {step.description}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {step.fields && step.fields.length > 0 ? (
                                        step.fields.map((field, fieldIndex) => (
                                            <Paper
                                                key={field.id || fieldIndex}
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    border: `1px solid ${stitchColors.outlineVariant}`,
                                                    borderRadius: 1.5,
                                                    bgcolor: stitchColors.surfaceContainerLowest,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            {fieldIndex + 1}. {field.label}
                                                        </Typography>
                                                        {field.required && (
                                                            <Chip label="Required" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
                                                        )}
                                                    </Box>
                                                    <Chip label={field.type} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} />
                                                </Box>

                                                {field.placeholder && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        Placeholder: "{field.placeholder}"
                                                    </Typography>
                                                )}

                                                {field.options && field.options.length > 0 && (
                                                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Options:</Typography>
                                                        {field.options.map((opt, oIdx) => (
                                                            <Chip key={oIdx} label={opt} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
                                                        ))}
                                                    </Box>
                                                )}

                                                {field.aiHelper && (
                                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#eff6ff', p: 1, borderRadius: 1 }}>
                                                        <AiIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                                        <Typography variant="caption" sx={{ color: '#1e40af' }}>
                                                            AI Helper: {field.aiHelper}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Paper>
                                        ))
                                    ) : (
                                        <Box sx={{ p: 2, bgcolor: stitchColors.surfaceContainerLowest, borderRadius: 1.5, border: `1px dashed ${stitchColors.outlineVariant}` }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {step.title.toLowerCase().includes('review')
                                                    ? 'Section Review Step: Automatically generates an interactive summary of all answers submitted in this section with Edit shortcuts.'
                                                    : 'No questions configured for this step.'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        sx={{ bgcolor: stitchColors.primary }}
                        onClick={() => {
                            setDetailsDialogOpen(false);
                            if (detailsQuestionnaire?._id) {
                                navigate(`/questionnaire-library/preview/${detailsQuestionnaire._id}`);
                            }
                        }}
                    >
                        View Live Form (User View)
                    </Button>
                    {detailsQuestionnaire && !detailsQuestionnaire.isActive && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => {
                                handleToggleActive(detailsQuestionnaire);
                                setDetailsDialogOpen(false);
                            }}
                        >
                            Set as Active Questionnaire
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setDetailsDialogOpen(false);
                            navigate(`/questionnaire-library/edit/${detailsQuestionnaire?._id}`);
                        }}
                    >
                        Edit Questionnaire
                    </Button>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Assign Dialog */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Questionnaire to Users</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Assigning: <strong>{selectedQuestionnaire?.title}</strong>
                    </Typography>
                    <FormControl fullWidth disabled={userNodesLoading || userNodes.length === 0}>
                        <InputLabel>Select Users</InputLabel>
                        <Select
                            multiple
                            value={selectedUserIds}
                            onChange={(e) => setSelectedUserIds(e.target.value as string[])}
                            renderValue={(selected) => {
                                const names = selected.map(id => {
                                    const u = userNodes.find(n => n._id === id);
                                    return u?.fullName || u?.email || id;
                                });
                                return names.join(', ');
                            }}
                        >
                            {userNodes.map((node) => (
                                <MenuItem key={node._id} value={node._id}>
                                    <Checkbox checked={selectedUserIds.includes(node._id)} />
                                    <ListItemText primary={node.fullName} secondary={node.email} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {userNodes.length === 0 && !userNodesLoading && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            No users found. Add users in the Users page first.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={assignMutation.isPending || selectedUserIds.length === 0}
                    >
                        {assignMutation.isPending ? 'Assigning...' : `Assign to ${selectedUserIds.length} User(s)`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete Questionnaire?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{deleteTarget?.title}"?
                    </Typography>
                    {deleteTarget?.isActive && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This is your currently active welcome questionnaire. Deleting it will cause your clients to fall back to the Global Welcome Questionnaire.
                        </Alert>
                    )}
                    {deleteTarget?.assignmentCounts?.total ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This questionnaire has {deleteTarget.assignmentCounts.total} assignments.
                            It will be safely deactivated and archived.
                        </Alert>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <GenerateQuestionnaireModal
                open={generateModalOpen}
                onClose={() => setGenerateModalOpen(false)}
                onGenerated={handleGeneratedDraft}
            />
        </Box>
    );
};

export default QuestionnaireManagement;
